import React, { useState, useEffect } from "react";
import {
  Users,
  Plus,
  Trash2,
  Pencil,
  X,
  Loader2,
  Building2,
  MapPin,
  Phone,
  Mail,
  FileText,
  Upload,
  Search,
  ChevronRight,
  AlertCircle,
  PenTool
} from "lucide-react";
import clientService from "../services/client.service";
import { getFilePreviewUrl } from "../utils/fileToken";

const DOCUMENT_TYPES = [
  "GST Certificate",
  "PAN Card",
  "IEC Copy",
  "Aadhar Card",
  "Other"
];

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSigUrl, setClientSigUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (selectedClient?.signature?.uploadedAt) {
      clientService.getSignatureUrl(selectedClient._id).then((url) => {
        if (!cancelled) setClientSigUrl(url);
      });
    } else {
      setClientSigUrl(null);
    }
    return () => { cancelled = true; };
  }, [selectedClient?._id, selectedClient?.signature?.uploadedAt]);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "", companyName: "", email: "", phone: "",
    address: "", city: "", state: "", pincode: "", gstin: ""
  });

  const [newClient, setNewClient] = useState({
    name: "",
    companyName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    gstin: ""
  });

  const [docData, setDocData] = useState({
    type: "",
    number: "",
    file: null
  });

  // Signature state
  const [signatureFile, setSignatureFile] = useState(null);
  const [signatureUploading, setSignatureUploading] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await clientService.getClients();
      if (response.success) {
        setClients(response.data.clients);
      }
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await clientService.createClient(newClient);
      if (response.success) {
        setClients([response.data.client, ...clients]);
        setShowAddModal(false);
        setNewClient({
          name: "", companyName: "", email: "", phone: "",
          address: "", city: "", state: "", pincode: "", gstin: ""
        });
      }
    } catch (error) {
      alert(error.response?.data?.message || "Failed to add client");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    try {
      await clientService.deleteClient(id);
      setClients(clients.filter(c => c._id !== id));
    } catch (error) {
      alert("Failed to delete client");
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setEditForm({
      name: client.name || "",
      companyName: client.companyName || "",
      email: client.email || "",
      phone: client.phone || "",
      address: client.address || "",
      city: client.city || "",
      state: client.state || "",
      pincode: client.pincode || "",
      gstin: client.gstin || ""
    });
    setShowEditModal(true);
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await clientService.updateClient(editingClient._id, editForm);
      if (response.success) {
        setClients(clients.map(c => c._id === editingClient._id ? response.data.client : c));
        setShowEditModal(false);
        setEditingClient(null);
      }
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update client");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (clientId, docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await clientService.deleteDocument(clientId, docId);
      fetchClients();
    } catch (error) {
      alert("Failed to delete document");
    }
  };

  const handleUploadSignature = async () => {
    if (!signatureFile) {
      alert('Please select a signature image');
      return;
    }

    setSignatureUploading(true);
    try {
      const formData = new FormData();
      formData.append('signature', signatureFile);
      const response = await clientService.uploadSignature(selectedClient._id, formData);
      if (response.success) {
        setSelectedClient(prev => ({
          ...prev,
          signature: {
            mimeType: response.data.signature.mimeType,
            originalName: response.data.signature.originalName,
            uploadedAt: response.data.signature.uploadedAt
          }
        }));
        alert('Signature uploaded successfully!');
      }
      setSignatureFile(null);
      fetchClients();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to upload signature');
    } finally {
      setSignatureUploading(false);
    }
  };

  const handleDeleteSignature = async () => {
    if (!window.confirm('Delete this signature?')) return;
    try {
      await clientService.deleteSignature(selectedClient._id);
      setSelectedClient(prev => ({ ...prev, signature: undefined }));
      alert('Signature deleted');
      fetchClients();
    } catch (error) {
      alert('Failed to delete signature');
    }
  };

  const handleUploadDoc = async (e) => {
    e.preventDefault();
    if (!docData.file || !docData.type) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("type", docData.type);
      formData.append("number", docData.number);
      formData.append("clientDocument", docData.file);

      const response = await clientService.uploadDocument(selectedClient._id, formData);
      if (response.success) {
        alert("Document uploaded successfully");
        setShowDocModal(false);
        fetchClients();
      }
    } catch (error) {
      alert("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.phone.includes(searchQuery)
  );

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Client Management</h1>
          <p className="text-gray-500 font-medium">Manage your B2B clients and their shipping details.</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-2xl font-bold shadow-lg hover:bg-blue-700 transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          <Plus className="w-5 h-5" /> Add New Client
        </button>
      </div>

      <div className="mb-6 relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search by name, company, or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="block w-full pl-11 pr-4 py-3 border-transparent bg-white shadow-sm rounded-2xl focus:ring-4 focus:ring-blue-50 focus:border-blue-500 outline-none transition-all font-medium"
        />
      </div>

      {loading && clients.length === 0 ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
        </div>
      ) : filteredClients.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => (
            <div key={client._id} className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all overflow-hidden group">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditClient(client)}
                      className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-xl transition-all"
                      title="Edit"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => { setSelectedClient(client); setShowDocModal(true); }}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                      title="Documents"
                    >
                      <FileText className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client._id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-1">{client.name}</h3>
                {client.companyName && (
                  <p className="text-sm font-bold text-blue-600 mb-4">{client.companyName}</p>
                )}

                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-sm text-gray-600 font-medium">
                    <Phone className="w-4 h-4 text-gray-400" />
                    {client.phone}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600 font-medium">
                    <Mail className="w-4 h-4 text-gray-400" />
                    {client.email || "No email provided"}
                  </div>
                  <div className="flex items-start gap-3 text-sm text-gray-600 font-medium">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                    <span className="line-clamp-2">{client.address}, {client.city}, {client.state} - {client.pincode}</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${client.isActive ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{client.isActive ? 'Active' : 'Inactive'}</span>
                  </div>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    {client.documents?.length || 0} Docs
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200">
          <Users className="w-16 h-16 text-gray-200 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900">No clients found</h3>
          <p className="text-gray-500 max-w-xs mx-auto mt-2">Add your first client to start printing their details on shipping labels.</p>
        </div>
      )}

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl p-8 md:p-10">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h4 className="text-2xl font-bold text-gray-900">Add New Client</h4>
                <p className="text-sm text-gray-500 font-medium">These details will appear as the "Shipper" on the label.</p>
              </div>
              <button 
                onClick={() => setShowAddModal(false)}
                className="p-3 hover:bg-gray-100 rounded-2xl transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAddClient} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">Contact Name</label>
                  <input required placeholder="Full Name" value={newClient.name} onChange={(e) => setNewClient({...newClient, name: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">Company Name</label>
                  <input placeholder="e.g. Acme Corp" value={newClient.companyName} onChange={(e) => setNewClient({...newClient, companyName: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">Phone Number</label>
                  <input required placeholder="10-digit mobile" value={newClient.phone} onChange={(e) => setNewClient({...newClient, phone: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">Email (Optional)</label>
                  <input type="email" placeholder="client@mail.com" value={newClient.email} onChange={(e) => setNewClient({...newClient, email: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">Full Address</label>
                <textarea required rows={3} placeholder="Building, Street, Area" value={newClient.address} onChange={(e) => setNewClient({...newClient, address: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold resize-none" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">Pincode</label>
                  <input required placeholder="6-digit" value={newClient.pincode} onChange={(e) => setNewClient({...newClient, pincode: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">City</label>
                  <input required placeholder="City" value={newClient.city} onChange={(e) => setNewClient({...newClient, city: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">State</label>
                  <input required placeholder="State" value={newClient.state} onChange={(e) => setNewClient({...newClient, state: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">GSTIN (Optional)</label>
                <input placeholder="22AAAAA0000A1Z5" value={newClient.gstin} onChange={(e) => setNewClient({...newClient, gstin: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className="w-full py-4 bg-gray-900 text-white rounded-[1.5rem] font-bold text-lg shadow-xl hover:bg-gray-800 transition-all flex items-center justify-center gap-2 hover:scale-[1.01] active:scale-[0.99]"
              >
                {loading && <Loader2 className="w-5 h-5 animate-spin"/>} Save Client Details
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditModal && editingClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl p-8 md:p-10">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h4 className="text-2xl font-bold text-gray-900">Edit Client</h4>
                <p className="text-sm text-gray-500 font-medium">{editingClient.name}</p>
              </div>
              <button
                onClick={() => { setShowEditModal(false); setEditingClient(null); }}
                className="p-3 hover:bg-gray-100 rounded-2xl transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateClient} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">Contact Name</label>
                  <input required placeholder="Full Name" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">Company Name</label>
                  <input placeholder="e.g. Acme Corp" value={editForm.companyName} onChange={(e) => setEditForm({...editForm, companyName: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">Phone Number</label>
                  <input required placeholder="10-digit mobile" value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">Email (Optional)</label>
                  <input type="email" placeholder="client@mail.com" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">Full Address</label>
                <textarea required rows={3} placeholder="Building, Street, Area" value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold resize-none" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">Pincode</label>
                  <input required placeholder="6-digit" value={editForm.pincode} onChange={(e) => setEditForm({...editForm, pincode: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">City</label>
                  <input required placeholder="City" value={editForm.city} onChange={(e) => setEditForm({...editForm, city: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">State</label>
                  <input required placeholder="State" value={editForm.state} onChange={(e) => setEditForm({...editForm, state: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">GSTIN (Optional)</label>
                <input placeholder="22AAAAA0000A1Z5" value={editForm.gstin} onChange={(e) => setEditForm({...editForm, gstin: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/30 outline-none transition-all font-semibold" />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-green-600 text-white rounded-[1.5rem] font-bold text-lg shadow-xl hover:bg-green-700 transition-all flex items-center justify-center gap-2 hover:scale-[1.01] active:scale-[0.99]"
              >
                {loading && <Loader2 className="w-5 h-5 animate-spin"/>} Update Client Details
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Documents Modal */}
      {showDocModal && selectedClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl p-8 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-2xl font-bold text-gray-900">Client Documents</h4>
                <p className="text-sm text-gray-500 font-medium">{selectedClient.name}</p>
              </div>
              <button 
                onClick={() => setShowDocModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
              {selectedClient.documents?.length > 0 ? (
                selectedClient.documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="text-sm font-bold text-gray-800">{doc.type}</p>
                        {doc.number && <p className="text-[10px] text-gray-500 font-medium">{doc.number}</p>}
                        <div className="flex items-center gap-2">
                          <p className="text-[10px] text-gray-400 font-bold uppercase">{doc.status}</p>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              getFilePreviewUrl(`/clients/${selectedClient._id}/documents/${doc._id}/file`).then((url) => {
                                if (url) window.open(url, '_blank', 'noopener,noreferrer');
                              });
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] text-blue-500 font-bold uppercase hover:underline"
                          >
                            View
                          </a>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteDocument(selectedClient._id, doc._id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                      title="Delete document"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-400 font-medium italic">No documents uploaded yet.</p>
                </div>
              )}
            </div>

            {/* Signature Section */}
            <div className="pt-4 border-t border-gray-100">
              <h5 className="font-bold text-gray-800 mb-3 text-sm flex items-center gap-2">
                <PenTool className="w-4 h-4" /> Client Signature
              </h5>
              {selectedClient.signature?.uploadedAt ? (
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-2xl border border-green-100 mb-2">
                  <div className="flex items-center gap-3">
                    <img
                      src={clientSigUrl}
                      alt="Client Signature"
                      className="h-12 max-w-[140px] object-contain bg-white rounded-lg border p-1"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div>
                      <p className="text-sm font-bold text-green-800">Signature uploaded</p>
                      <p className="text-[10px] text-green-600">
                        {selectedClient.signature.originalName || 'signature.png'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleDeleteSignature}
                    className="p-2 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
                    title="Delete signature"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="mb-3 p-3 bg-amber-50 rounded-xl border border-amber-100 text-xs text-amber-700">
                  No signature uploaded. Upload a PNG/JPG image of the authorized signatory&apos;s signature to appear on invoices.
                </div>
              )}

              <div className="flex items-center gap-3">
                <div className="relative border-2 border-dashed border-gray-200 rounded-xl px-4 py-3 text-center hover:border-blue-400 transition-all cursor-pointer bg-gray-50 flex-1">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={(e) => setSignatureFile(e.target.files[0])}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-[11px] font-bold text-gray-500">
                    {signatureFile ? signatureFile.name : 'Choose signature image'}
                  </p>
                </div>
                <button
                  onClick={handleUploadSignature}
                  disabled={signatureUploading || !signatureFile}
                  className="px-4 py-3 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 disabled:opacity-50 transition-all flex items-center gap-2 whitespace-nowrap"
                >
                  {signatureUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload
                </button>
              </div>
              <p className="text-[10px] text-gray-400 mt-2 ml-1">Recommended: PNG with transparent background, max 2MB</p>
            </div>

            <div className="pt-4 border-t border-gray-100 mt-4">
              <h5 className="font-bold text-gray-800 mb-4 text-sm">Upload New Document</h5>
              <form onSubmit={handleUploadDoc} className="space-y-4">
                <select
                  required
                  value={docData.type}
                  onChange={(e) => setDocData({...docData, type: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/20 outline-none transition-all font-semibold"
                >
                  <option value="">Select Document Type</option>
                  {DOCUMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input
                  type="text"
                  placeholder="Document Number (e.g. ABCDE1234F)"
                  value={docData.number}
                  onChange={(e) => setDocData({...docData, number: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50/20 outline-none transition-all font-semibold"
                />
                <div className="relative border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-blue-400 transition-all cursor-pointer bg-gray-50">
                  <input 
                    type="file" 
                    required
                    onChange={(e) => setDocData({...docData, file: e.target.files[0]})}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                  <p className="text-[11px] font-bold text-gray-500">
                    {docData.file ? docData.file.name : "Click to upload PDF or Image"}
                  </p>
                </div>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin"/>} Upload Document
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManagement;
